<template>
  <content-dialog
    v-model="dialogOpen"
    title="Debug Log"
    width="750px"
    no-backdrop-dismiss
    fullscreen-mobile
  >
    <q-card-section>
      <q-item-label class="text-bold">
        Use the debug log to help report errors to the {{ siteName }} team.
      </q-item-label>

      <q-separator spaced />

      <q-item-label class="text-bold">
        <pre>Endpoint: {{ url }} ({{ method.toUpperCase() }})</pre>
      </q-item-label>

      <q-item-label v-if="message" class="text-red-7 text-bold">
        {{ message }}
      </q-item-label>

      <div
        v-if="Object.keys(errors).length > 0"
        class="overflow-auto"
      >
        <pre>{{ JSON.stringify(errors, null, 2) }}</pre>
      </div>
    </q-card-section>

    <template #bottom>
      <q-toolbar>
        <q-space />
        <q-btn
          flat
          size="12px"
          icon="mdi-download"
          label="Download Debug File"
          @click="exportToFile"
        />
      </q-toolbar>
    </template>
  </content-dialog>
</template>

<script>
import { exportFile } from 'quasar'
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { FileOpener } from '@capawesome-team/capacitor-file-opener'

// Refer to https://quasar.dev/quasar-plugins/dialog#Invoking-custom-component
export default {
  props: {
    url: {
      type: String,
      required: true
    },
    method: {
      type: String,
      required: true
    },
    message: {
      type: String,
      default: null
    },
    sentData: {
      type: [String, Number, Object, Array],
      default: null
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  emits: [
    // REQUIRED
    'ok', 'hide'
  ],
  data () {
    return {
      dialogOpen: false
    }
  },
  computed: {
    debugPayload () {
      return {
        url: this.url,
        method: this.method,
        message: this.message,
        errors: this.errors,
        sentData: this.sentData
      }
    }
  },
  methods: {
    // following method is REQUIRED
    // (don't change its name --> "show")
    show () {
      this.dialogOpen = true
    },
    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide () {
      this.dialogOpen = false
    },
    onOKClick () {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok')
      // or with payload: this.$emit('ok', { ... })
      // then hiding dialog
      this.hide()
    },
    onCancelClick () {
      // we just need to hide dialog
      this.hide()
    },
    async exportToFile () {
      this.$loading.show()

      // If "password" is included in the data blob, do not print information to file
      let dataBlob = JSON.stringify(this.debugPayload, null, 2)
      if (dataBlob.includes('password')) {
        dataBlob = 'Redacted (sensitive information)'
      }

      if (this.$q.platform.is.capacitor) {
        try {
          const state = await Filesystem.writeFile({
            path: 'DebugLog-' + Date.now() + '.txt',
            data: dataBlob,
            directory: Directory.Documents,
            encoding: Encoding.UTF8
          })
          if (state && state.uri) {
            this.$q.notify({
              color: 'green',
              message: 'File saved to filesystem'
            })

            await FileOpener.openFile({
              path: state.uri
            })
          }
          else {
            throw new Error('Failed to write file')
          }
        }
        catch (err) {
          this.$q.notify({
            color: 'red',
            message: err.message
          })
        }
        finally {
          this.$loading.hide()
        }
        return
      }

      exportFile(
        'DebugLog.txt',
        dataBlob,
        {
          mimeType: 'application/json'
        }
      )

      this.$loading.hide()
    }
  }
}
</script>
